import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixPromotePublisherV1Item from './promote-publisher-v1-item-items.http.js';

/** An item is a social media post managed through the Publisher API. An item can be a published post, a scheduled post, or a draft, as indicated by its `status`, and it targets a single connected social channel such as Instagram, Facebook, or TikTok. */
export interface Item extends ItemDataOneOf {
  /** Instagram post content. */
  instagramPost?: InstagramPost;
  /** Instagram story content. */
  instagramStory?: InstagramStory;
  /** Facebook post content. */
  facebookPost?: FacebookPost;
  /** Facebook reel content. */
  facebookReel?: FacebookReel;
  /** Facebook story content. */
  facebookStory?: FacebookStory;
  /** YouTube video content. */
  youtubeVideo?: YouTubeVideo;
  /** YouTube short content. */
  youtubeShort?: YouTubeShort;
  /** LinkedIn post content. */
  linkedinPost?: LinkedinPost;
  /**
   * Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @deprecated Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @targetRemovalDate 2026-07-31
   */
  twitterPost?: TwitterPost;
  /** Pinterest post content. */
  pinterestPost?: PinterestPost;
  /** Google Business Profile post content. */
  gbpPost?: GbpPost;
  /** TikTok video content. */
  tiktokVideo?: TikTokVideo;
  /** TikTok photo content. */
  tiktokPhoto?: TikTokPhoto;
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Social channel the item is published to, and the account within that channel. */
  channel?: Channel;
  /**
   * Item format. The `type` and the content set in `data` must match a combination supported by the item's `channel.name`:
   *
   * - Instagram: `POST` with `instagramPost`, or `STORY` with `instagramStory`.
   * - Facebook: `POST` with `facebookPost`, `REEL` with `facebookReel`, or `STORY` with `facebookStory`.
   * - YouTube: `VIDEO` with `youtubeVideo`, or `SHORT` with `youtubeShort`.
   * - LinkedIn: `POST` with `linkedinPost`.
   * - X (Twitter): `POST` with `twitterPost`. Deprecated: No longer functional as of July 31, 2026.
   * - Pinterest: `POST` with `pinterestPost`.
   * - Google Business Profile: `POST` with `gbpPost`.
   * - TikTok: `POST` with `tiktokPhoto`, or `VIDEO` with `tiktokVideo`.
   */
  type?: ItemTypeWithLiterals;
  /**
   * Current status of the item in its lifecycle.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Date and time the item was published. Returned only after the item is published.
   * @readonly
   */
  publishedDate?: Date | null;
  /** Scheduling info. Set to schedule the item for a future date instead of publishing it immediately. */
  schedulingInfo?: SchedulingInfo;
  /** Identifies what created the item. */
  consumerInfo?: ConsumerInfo;
  /** Caller-defined reference that uniquely identifies the item. Can be used to prevent duplicate publishing: an attempt to publish a second item with the same `referenceId` fails. Can also be used as a UTM campaign ID. */
  referenceId?: string | null;
  /** Aggregated analytics for the site, attributed to traffic from this post. */
  siteAnalytics?: SiteAnalytics;
  /** Summary of the post's performance on the social channel, such as impressions and interactions. */
  insightsSummary?: InsightsSummary;
  /** Creation and update dates, returned for draft items. */
  draftInfo?: DraftInfo;
  /** Whether the item was generated as an AI suggestion. */
  suggested?: boolean;
  /** Whether the item is a post created natively on the social channel rather than published through Wix. */
  external?: boolean;
  /**
   * Details of why the item failed to publish. Returned only when `status` is `FAILED`.
   * @readonly
   */
  publicationError?: PublicationErrorDetails;
  /**
   * Reference to the site content the post promotes, such as a product, a blog post, or an event.
   * Set per channel.
   */
  siteAssetRef?: AssetReference;
  /**
   * ID of the item this item was derived from, when publishing produced several items
   * from one. Currently set only by `PublishMultiStoryDraft`, which splits a multi-media
   * story draft into one published item per frame: every resulting item carries the
   * originating draft's ID here. Lets a consumer re-correlate the new items with whatever
   * it had recorded against the original draft, which no longer exists after the split.
   * Set by the publisher, never by the caller.
   * @format GUID
   * @readonly
   */
  originItemId?: string | null;
  /**
   * Engagement automations on the published post. Updates replace the whole list. Facebook posts only.
   * @internal
   */
  automationsWrapper?: ItemAutomationsWrapper;
}

/** @oneof */
export interface ItemDataOneOf {
  /** Instagram post content. */
  instagramPost?: InstagramPost;
  /** Instagram story content. */
  instagramStory?: InstagramStory;
  /** Facebook post content. */
  facebookPost?: FacebookPost;
  /** Facebook reel content. */
  facebookReel?: FacebookReel;
  /** Facebook story content. */
  facebookStory?: FacebookStory;
  /** YouTube video content. */
  youtubeVideo?: YouTubeVideo;
  /** YouTube short content. */
  youtubeShort?: YouTubeShort;
  /** LinkedIn post content. */
  linkedinPost?: LinkedinPost;
  /**
   * Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @deprecated Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @targetRemovalDate 2026-07-31
   */
  twitterPost?: TwitterPost;
  /** Pinterest post content. */
  pinterestPost?: PinterestPost;
  /** Google Business Profile post content. */
  gbpPost?: GbpPost;
  /** TikTok video content. */
  tiktokVideo?: TikTokVideo;
  /** TikTok photo content. */
  tiktokPhoto?: TikTokPhoto;
}

/** Supported social channel. */
export enum ChannelName {
  INSTAGRAM = 'INSTAGRAM',
  FACEBOOK = 'FACEBOOK',
  YOUTUBE = 'YOUTUBE',
  LINKEDIN = 'LINKEDIN',
  /** Deprecated: No longer functional as of July 31, 2026. */
  TWITTER = 'TWITTER',
  PINTEREST = 'PINTEREST',
  /** Google Business Profile. */
  GBP = 'GBP',
  TIKTOK = 'TIKTOK',
}

/** @enumType */
export type ChannelNameWithLiterals =
  | ChannelName
  | 'INSTAGRAM'
  | 'FACEBOOK'
  | 'YOUTUBE'
  | 'LINKEDIN'
  | 'TWITTER'
  | 'PINTEREST'
  | 'GBP'
  | 'TIKTOK';

export interface PublishingTarget {
  /**
   * ID of the publishing target.
   * @maxLength 128
   */
  _id?: string | null;
  /**
   * Display name of the publishing target.
   * @maxLength 2000
   */
  displayName?: string | null;
  /**
   * URL of the publishing target's picture.
   * @format WEB_URL
   */
  pictureUrl?: string | null;
}

export enum ConsumerType {
  METASITE = 'METASITE',
}

/** @enumType */
export type ConsumerTypeWithLiterals = ConsumerType | 'METASITE';

/** Type of site asset. */
export enum Type {
  /** A Wix Blog post. */
  BLOG_POST = 'BLOG_POST',
  /** A Wix Events event. */
  EVENT = 'EVENT',
  /** A Wix Stores product. */
  STORES_PRODUCT = 'STORES_PRODUCT',
  /** A Wix Bookings service. */
  BOOKINGS_SERVICE = 'BOOKINGS_SERVICE',
  /** A Wix Stores coupon. */
  STORES_COUPON = 'STORES_COUPON',
  /** A Wix Stores category. */
  STORES_CATEGORY = 'STORES_CATEGORY',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'BLOG_POST'
  | 'EVENT'
  | 'STORES_PRODUCT'
  | 'BOOKINGS_SERVICE'
  | 'STORES_COUPON'
  | 'STORES_CATEGORY';

export interface ItemAutomation
  extends ItemAutomationTriggerOneOf,
    ItemAutomationActionOneOf {
  /** Triggered by a comment on the post. */
  commentTrigger?: CommentTrigger;
  /** Sends the commenter an automated private reply. */
  sendPrivateReply?: SendPrivateReplyAction;
  /** Whether the automation is active. */
  enabled?: boolean;
}

/** @oneof */
export interface ItemAutomationTriggerOneOf {
  /** Triggered by a comment on the post. */
  commentTrigger?: CommentTrigger;
}

/** @oneof */
export interface ItemAutomationActionOneOf {
  /** Sends the commenter an automated private reply. */
  sendPrivateReply?: SendPrivateReplyAction;
}

export interface CommentTrigger {
  /**
   * The automation fires when a comment contains any of these phrases (case-insensitive).
   * @maxSize 20
   * @maxLength 200
   */
  phrases?: string[];
}

export interface SendPrivateReplyAction {
  /**
   * Message text of the private reply.
   * @maxLength 2000
   */
  message?: string | null;
}

export interface Channel {
  /** Social channel the item is published to. */
  name?: ChannelNameWithLiterals;
  /** ID of the connected account the item is published to. */
  accountId?: string | null;
  /**
   * Display name of the connected account the item is published to.
   * @readonly
   */
  accountName?: string | null;
  /**
   * ID of the post on the social channel. Returned after the item is published.
   * @readonly
   */
  externalItemId?: string | null;
  /**
   * URL of the post on the social channel. Returned after the item is published.
   * @readonly
   * @format WEB_URL
   */
  externalItemUrl?: string | null;
  /** Specific destination within the account, such as a Facebook page or a Pinterest board. */
  publishingTarget?: PublishingTarget;
}

/** Item format. */
export enum ItemType {
  /** A standard post. */
  POST = 'POST',
  /** A short-form vertical video reel. */
  REEL = 'REEL',
  /** A video. */
  VIDEO = 'VIDEO',
  /** A short-form vertical video. */
  SHORT = 'SHORT',
  /** A story that's available for a limited time. */
  STORY = 'STORY',
}

/** @enumType */
export type ItemTypeWithLiterals =
  | ItemType
  | 'POST'
  | 'REEL'
  | 'VIDEO'
  | 'SHORT'
  | 'STORY';

/** Status of an item in its lifecycle. */
export enum Status {
  /** Published to the social channel. */
  PUBLISHED = 'PUBLISHED',
  /** Scheduled to be published at a future date. */
  SCHEDULED = 'SCHEDULED',
  /** Scheduling was canceled before the item was published. */
  CANCELED = 'CANCELED',
  /** Publishing failed. */
  FAILED = 'FAILED',
  /** Deleted. */
  DELETED = 'DELETED',
  /** Publishing is in progress. */
  PROCESSING = 'PROCESSING',
  /** Saved as a draft, not yet published. */
  DRAFT = 'DRAFT',
  /** Queued for publishing as part of a bulk operation. */
  IN_QUEUE = 'IN_QUEUE',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'PUBLISHED'
  | 'SCHEDULED'
  | 'CANCELED'
  | 'FAILED'
  | 'DELETED'
  | 'PROCESSING'
  | 'DRAFT'
  | 'IN_QUEUE';

export interface SchedulingInfo {
  /** Date and time the item is scheduled to be published. */
  scheduledDate?: Date | null;
  /**
   * Date and time the item was scheduled.
   * @readonly
   */
  schedulingTimestamp?: Date | null;
}

export interface ConsumerInfo {
  /**
   * The consumer type
   * @internal
   */
  type?: ConsumerTypeWithLiterals;
  /**
   * The consumer id
   * @internal
   */
  _id?: string | null;
  /**
   * Stable name identifying what created the item, for example `social-marketing` for
   * the Social Media Marketing dashboard. Set on creation only. Update Item ignores it.
   */
  name?: string | null;
  /**
   * The consumer asset id e.g. store item, an event etc'
   * @internal
   */
  assetId?: string | null;
}

export interface InstagramPost extends InstagramPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Caption for the post. Can include hashtags (for example, `#crazywildebeest`) and mentions of Instagram users (for example, `@natgeo`). Mentioned users are notified when the post is published. Supports up to 30 hashtags and 20 mentions.
   * @maxLength 2200
   */
  caption?: string | null;
  /**
   * The post URL on Instagram.
   * @readonly
   */
  postUrl?: string | null;
  /**
   * Insights from Instagram.
   * @readonly
   */
  insights?: InstagramInsights;
}

/** @oneof */
export interface InstagramPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface MediaWrapper {
  /** List of media items. */
  media?: Media[];
}

export interface Media {
  /** Media type. */
  type?: MediaTypeWithLiterals;
  /**
   * Media URL.
   * @format WEB_URL
   */
  url?: string | null;
  /**
   * Media Manager file ID backing the media. Available only when the media is stored in the Media Manager.
   * @maxLength 1000
   */
  fileId?: string | null;
  /**
   * Set on media whose url is temporary and should be imported into the user's Media Manager.
   * May be passed on input, and may also be returned when relevant so the client can pass it back
   * on the item write. When true, the server imports the temporary url into the user's Media Manager
   * on publish/schedule/save and stores the resulting url + file_id.
   * @internal
   */
  requiresPersistence?: boolean | null;
}

export enum MediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
}

/** @enumType */
export type MediaTypeWithLiterals = MediaType | 'IMAGE' | 'VIDEO';

export interface InstagramInsights {
  /** Number of likes. */
  likes?: number;
  /** Number of engagements. */
  engagement?: number;
  /** Number of impressions. */
  impressions?: number;
  /** Number of plays. */
  plays?: number;
  /** Number of accounts reached. */
  reach?: number;
  /** Number of comments. */
  comments?: number;
  /** Number of shares. */
  shares?: number;
  /** Number of saves. */
  saved?: number;
  /** Total number of interactions. */
  totalInteractions?: number;
}

export interface InstagramStory {
  /**
   * The story URL on Instagram.
   * @readonly
   * @format WEB_URL
   */
  storyUrl?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * insights data from Instagram.
   * @readonly
   */
  insights?: InstagramStoryInsights;
}

export interface InstagramStoryInsights {
  /** Number of impressions. */
  impressions?: number;
  /** Number of accounts reached. */
  reach?: number;
  /** Number of replies. */
  replies?: number;
}

export interface FacebookPost extends FacebookPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /** The page ID to publish the post to. */
  pageId?: string | null;
  /** Caption for the post. */
  caption?: string | null;
  /**
   * The post URL on Facebook.
   * @readonly
   */
  postUrl?: string | null;
  /** Link preview metadata. Available only when the post contains a link. */
  linkMetadata?: LinkMetadata;
  /**
   * Insights from Facebook.
   * @readonly
   */
  insights?: FacebookInsights;
}

/** @oneof */
export interface FacebookPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface LinkMetadata {
  /** Thumbnail URL for the link preview. */
  thumbnailUrl?: string | null;
  /** Title for the link preview. */
  title?: string | null;
  /** Description for the link preview. */
  description?: string | null;
}

export interface FacebookInsights {
  /** Impression counts. */
  impressions?: FacebookImpressions;
  /** Interaction counts. */
  interactions?: FacebookInteractions;
  /** Reaction counts. */
  reactions?: FacebookReactions;
  /** Activity counts. */
  activity?: FacebokActivity;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
}

export interface FacebookImpressions {
  /** Number of unique impressions. */
  unique?: number;
  /** Total number of impressions. */
  total?: number;
}

export interface FacebookInteractions {
  /** Number of unique interactions. */
  unique?: number;
  /** Total number of interactions. */
  total?: number;
}

export interface FacebookReactions {
  /** Number of "like" reactions. */
  like?: number;
  /** Number of "love" reactions. */
  love?: number;
  /** Number of "wow" reactions. */
  wow?: number;
  /** Number of "haha" reactions. */
  haha?: number;
  /** Number of "sad" reactions. */
  sad?: number;
  /** Number of "angry" reactions. */
  anger?: number;
}

export interface FacebokActivity {
  /** Number of shares. */
  share?: number;
  /** Number of likes. */
  like?: number;
  /** Number of comments. */
  comment?: number;
}

export interface FacebookReel {
  /** The page ID to publish the reel to. */
  pageId?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** A description for the reel. */
  description?: string | null;
  /**
   * Insights from Facebook.
   * @readonly
   */
  insights?: FacebookInsights;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface FacebookStory {
  /**
   * The page ID to publish the story to.
   * @maxLength 1024
   */
  pageId?: string | null;
  /**
   * The story URL on Facebook.
   * @readonly
   * @format WEB_URL
   */
  storyUrl?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from Facebook.
   * @readonly
   */
  insights?: FacebookInsights;
}

export interface YouTubeVideo {
  /** The channel ID to publish the video to. */
  channelId?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** A title for the video. */
  title?: string | null;
  /** A description for the video. */
  description?: string | null;
  /** A category for the video. */
  categoryId?: string | null;
  /** Whether to notify channel subscribers when the video is published. */
  notifySubscribers?: boolean;
  /**
   * Insights data from YouTube.
   * @readonly
   */
  insights?: YoutubeInsights;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface YoutubeInsights {
  /** Number of views. */
  views?: number;
  /** Number of comments. */
  comments?: number;
  /** Number of likes. */
  likes?: number;
  /** Number of dislikes. */
  dislikes?: number;
  /** Number of shares. */
  shares?: number;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
}

export interface YouTubeShort {
  /** The channel ID to publish the short to. */
  channelId?: string | null;
  /** The short URL. */
  videoUrl?: string | null;
  /** A title for the short. */
  title?: string | null;
  /** A description for the short. */
  description?: string | null;
  /** A category for the short. */
  categoryId?: string | null;
  /** Whether to notify channel subscribers when the short is published. */
  notifySubscribers?: boolean;
  /**
   * Insights data from YouTube.
   * @readonly
   */
  insights?: YoutubeInsights;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface LinkedinPost extends LinkedinPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /** Author ID of the post. Can be an organization page or a personal feed. */
  authorId?: string | null;
  /** Caption for the post. */
  caption?: string | null;
  /** Link preview metadata. Available only when the post contains a link. */
  linkMetadata?: LinkMetadata;
  /**
   * Insights from LinkedIn.
   * @readonly
   */
  insights?: LinkedinPostInsights;
}

/** @oneof */
export interface LinkedinPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

export interface LinkedinPostInsights {
  /** Number of unique impressions. */
  uniqueImpressions?: number;
  /** Total number of impressions. */
  impression?: number;
  /** Number of shares. */
  shares?: number;
  /** Engagement rate. */
  engagement?: number;
  /** Number of clicks. */
  clicks?: number;
  /** Number of likes. */
  likes?: number;
  /** Number of comments. */
  comments?: number;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
}

/** Deprecated: No longer functional as of July 31, 2026. */
export interface TwitterPost extends TwitterPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /** Caption for the post. */
  caption?: string | null;
  /** Link preview metadata. Available only when the post contains a link. */
  linkMetadata?: LinkMetadata;
  /**
   * Insights from X (Twitter).
   * @readonly
   */
  insights?: TwitterInsights;
}

/** @oneof */
export interface TwitterPostUrlOneOf {
  /** The image URL. */
  imageUrl?: string | null;
  /** The video URL. */
  videoUrl?: string | null;
  /** The link URL. */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
}

/** Deprecated: No longer functional as of July 31, 2026. */
export interface TwitterInsights {
  /** Number of retweets. */
  retweets?: number;
  /** Number of replies. */
  replies?: number;
  /** Number of likes. */
  likes?: number;
  /** Number of quote posts. */
  quotes?: number;
  /** Number of bookmarks. */
  bookmarks?: number;
  /** Number of impressions. */
  impressions?: number;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
}

export interface PinterestPost {
  /**
   * The board ID to publish the post to.
   * @maxLength 128
   */
  boardId?: string | null;
  /**
   * The title of the post.
   * @maxLength 100
   */
  title?: string | null;
  /**
   * The description of the post.
   * @maxLength 800
   */
  description?: string | null;
  /**
   * The link to include in the post.
   * @maxLength 2048
   */
  link?: string | null;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from Pinterest.
   * @readonly
   */
  insights?: PinterestInsights;
}

export interface PinterestInsights {
  /** Number of impressions. */
  impressions?: number;
  /** Number of clicks. */
  clicks?: number;
  /** Number of saves. */
  saves?: number;
  /** Number of comments. */
  comments?: number;
  /** Number of reactions. */
  reactions?: number;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
}

export interface GbpPost {
  /**
   * The location ID to publish the post to.
   * @maxLength 128
   */
  locationId?: string | null;
  /**
   * Account (group) ID of the location.
   * @maxLength 128
   */
  locationAccountId?: string | null;
  /**
   * The description of the post.
   * @maxLength 1500
   */
  description?: string | null;
  /** Call to action type and URL to include in the post. */
  callToAction?: GbpPostCallToAction;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from Google Business Profile.
   * @readonly
   */
  insights?: GbpInsights;
}

export interface GbpPostCallToAction {
  /** Type of the call to action. */
  actionType?: GbpPostCallToActionTypeWithLiterals;
  /**
   * URL of the call to action.
   * @maxLength 2048
   */
  url?: string | null;
}

/** Type of call to action button shown on the post. */
export enum GbpPostCallToActionType {
  BOOK = 'BOOK',
  ORDER = 'ORDER',
  SHOP = 'SHOP',
  LEARN_MORE = 'LEARN_MORE',
  SIGN_UP = 'SIGN_UP',
}

/** @enumType */
export type GbpPostCallToActionTypeWithLiterals =
  | GbpPostCallToActionType
  | 'BOOK'
  | 'ORDER'
  | 'SHOP'
  | 'LEARN_MORE'
  | 'SIGN_UP';

export interface GbpInsights {
  /** Number of shares. Google Business Profile post insights aren't currently available, so this returns `-1`. */
  shares?: number;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
}

export interface TikTokVideo {
  /**
   * The account ID to publish the video to.
   * @maxLength 128
   */
  accountId?: string | null;
  /** Privacy level for the video. */
  privacyLevel?: TikTokPrivacyLevelWithLiterals;
  /**
   * A description for the video.
   * @maxLength 2200
   */
  description?: string | null;
  /** Whether comments are disabled for the video. */
  disableComment?: boolean;
  /** Whether duets are disabled for the video. */
  disableDuet?: boolean;
  /** Whether stitches are disabled for the video. */
  disableStitch?: boolean;
  /** Whether the content is a paid partnership promoting a third-party business. */
  brandContentToggle?: boolean;
  /** Whether the content promotes the creator's own business. */
  brandOrganicToggle?: boolean;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from TikTok.
   * @readonly
   */
  insights?: TikTokInsights;
}

export enum TikTokPrivacyLevel {
  PUBLIC_TO_EVERYONE = 'PUBLIC_TO_EVERYONE',
  FOLLOWER_OF_CREATOR = 'FOLLOWER_OF_CREATOR',
  MUTUAL_FOLLOW_FRIENDS = 'MUTUAL_FOLLOW_FRIENDS',
  SELF_ONLY = 'SELF_ONLY',
}

/** @enumType */
export type TikTokPrivacyLevelWithLiterals =
  | TikTokPrivacyLevel
  | 'PUBLIC_TO_EVERYONE'
  | 'FOLLOWER_OF_CREATOR'
  | 'MUTUAL_FOLLOW_FRIENDS'
  | 'SELF_ONLY';

export interface TikTokInsights {
  /** Number of views. */
  views?: number;
  /** Number of likes. */
  likes?: number;
  /** Number of comments. */
  comments?: number;
  /** Number of shares. */
  shares?: number;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
}

export interface TikTokPhoto {
  /**
   * The account ID to publish the photo to.
   * @maxLength 128
   */
  accountId?: string | null;
  /** Privacy level for the photo. */
  privacyLevel?: TikTokPrivacyLevelWithLiterals;
  /**
   * A title for the photo.
   * @maxLength 90
   */
  title?: string | null;
  /**
   * A description for the photo.
   * @maxLength 4000
   */
  description?: string | null;
  /** Whether comments are disabled for the photo. */
  disableComment?: boolean;
  /** Whether to automatically add music to the photo. */
  autoAddMusic?: boolean;
  /** Whether the content is a paid partnership promoting a third-party business. */
  brandContentToggle?: boolean;
  /** Whether the content promotes the creator's own business. */
  brandOrganicToggle?: boolean;
  /** The media list. */
  mediaWrapper?: MediaWrapper;
  /**
   * Insights from TikTok.
   * @readonly
   */
  insights?: TikTokInsights;
}

export interface SiteAnalytics {
  /** Number of site sessions attributed to this post. */
  traffic?: number;
  /** Number of page views attributed to this post. */
  pageView?: number;
  /** Site bounce rate attributed to this post. */
  siteBounceRate?: number;
  /** Revenue attributed to this post. */
  revenue?: number;
  /** Conversion funnel data attributed to this post. */
  conversionFunnel?: ConversionFunnel;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
}

export interface ConversionFunnel {
  /** Number of sessions that viewed the cart. */
  cartViewsSessions?: number;
  /** Number of sessions that reached checkout. */
  checkOutSessions?: number;
  /** Number of sessions that placed a store order. */
  storeOrdersSessions?: number;
  /** Number of confirmed bookings. */
  bookingsConfirmed?: number;
}

export interface InsightsSummary {
  /** Number of unique impressions (reach). */
  impressions?: number;
  /** Number of interactions, such as likes and comments. */
  interactions?: number;
  /** Date and time the insights were last updated. */
  _updatedDate?: Date | null;
  /** Total number of impressions. */
  impressionsTotal?: number;
}

export interface DraftInfo {
  /**
   * Date and time the draft was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the draft was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface PublicationErrorDetails {
  /**
   * Error message shown when publishing fails.
   * @readonly
   * @maxLength 500
   */
  message?: string | null;
}

export interface AssetReference {
  /** Type of the site asset (for example, product or blog post). See `SiteAsset.Type` for the closed set of values. */
  siteAssetRefType?: TypeWithLiterals;
  /**
   * ID of the asset within its provider's data store.
   * @maxLength 256
   */
  _id?: string | null;
  /**
   * Display name of the asset (for example, the product title or blog post title), cached at post-creation time.
   * @maxLength 3000
   */
  name?: string;
}

export interface ItemAutomationsWrapper {
  /**
   * Automations on the item.
   * @maxSize 10
   */
  automations?: ItemAutomation[];
}

export interface PublishItemRequest {
  /** Item to publish. */
  item?: Item;
  itemExtras?: ItemExtras;
}

export interface ItemExtras {
  /**
   * Where the user began the flow to open the publisher. For example, `stores.product-list`.
   * @maxLength 100
   */
  userOrigin?: string | null;
}

export interface PublishItemResponse {
  /** Published item. */
  item?: Item;
}

/** Triggered when publishing starts for an item. */
export interface ItemPublishStarted {
  /** Item being published. */
  item?: Item;
}

/** Triggered when an item is successfully published to its social channel. */
export interface ItemPublished {
  /** Published item. */
  item?: Item;
}

/** Triggered when an item fails to publish to its social channel. */
export interface ItemPublishFailed {
  /** Item that failed to publish. See `publicationError` for the reason. */
  item?: Item;
}

/** Triggered when an item is scheduled to be published at a future date. */
export interface ItemPublishScheduled {
  /** Scheduled item. */
  item?: Item;
}

export interface PublishItemByIdRequest {
  /**
   * ID of the item to publish.
   * @format GUID
   */
  _id: string | null;
  /** Date and time to publish the item. If omitted, the item is published immediately. */
  scheduledDate?: Date | null;
}

export interface PublishItemByIdResponse {
  /** Published item. */
  item?: Item;
}

export interface CreateDraftItemRequest {
  /** Item to create as a draft. */
  item?: Item;
  itemExtras?: ItemExtras;
}

export interface CreateDraftItemResponse {
  /** Created draft item. */
  item?: Item;
}

/** GetOrCreateWixelBindingByFileRequest */
export interface GetOrCreateWixelBindingByFileRequest {
  /**
   * Publisher draft id that owns the media file
   * @format GUID
   */
  draftId?: string;
  /**
   * Media Manager file id to open in Wixel
   * @maxLength 1000
   */
  fileId?: string;
}

/** GetOrCreateWixelBindingByFileResponse */
export interface GetOrCreateWixelBindingByFileResponse {
  /**
   * Wixel editor URL with the bound asset selected
   * @format WEB_URL
   */
  url?: string;
  /**
   * Wixel binding id
   * @format GUID
   */
  bindingId?: string | null;
  /**
   * Wixel asset id
   * @format GUID
   */
  assetId?: string | null;
  /**
   * Wixel project id
   * @format GUID
   */
  projectId?: string | null;
}

/** PublishDraftRequest */
export interface PublishDraftRequest {
  /**
   * The draft item id to publish
   * @format GUID
   */
  _id?: string;
}

/** PublishDraftResponse */
export interface PublishDraftResponse {
  /** The published item */
  item?: Item;
}

/** PublishMultiStoryDraftRequest */
export interface PublishMultiStoryDraftRequest {
  /**
   * ID of the multi-story draft to publish.
   * @format GUID
   */
  _id: string;
  /** Scheduling info applied to all story items. If set, the story items are scheduled instead of published immediately. */
  schedulingInfo?: SchedulingInfo;
}

/** PublishMultiStoryDraftResponse */
export interface PublishMultiStoryDraftResponse {
  /** Results, one per story item handled in this call, in story order. Story items already published by a previous call aren't included. */
  results?: BulkItemsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkItemsResult {
  /** Metadata for the individual item's publish result. */
  itemMetadata?: ItemMetadata;
  /** Published item. Returned only when `returnEntity` is `true` in the request. */
  item?: Item;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

/** isEligibleForSchedulingRequest */
export interface IsEligibleForSchedulingRequest {}

/** isEligibleForSchedulingResponse */
export interface IsEligibleForSchedulingResponse {
  isEligible?: boolean;
  reason?: SchedulingRestrictedReasonWithLiterals;
}

export enum SchedulingRestrictedReason {
  NO_SOCIAL_POSTS_SCHEDULING = 'NO_SOCIAL_POSTS_SCHEDULING',
}

/** @enumType */
export type SchedulingRestrictedReasonWithLiterals =
  | SchedulingRestrictedReason
  | 'NO_SOCIAL_POSTS_SCHEDULING';

export interface GetItemRequest {
  /**
   * ID of the item to retrieve.
   * @format GUID
   */
  _id: string;
}

export interface GetItemResponse {
  /** Retrieved item. */
  item?: Item;
}

export interface GetItemsRequest {
  /**
   * IDs of the items to retrieve.
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
}

export interface GetItemsResponse {
  /** Retrieved items. */
  items?: Item[];
}

export interface QueryItemsRequest {
  /** Query options, including paging, filtering, and sorting. */
  query: Query;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryItemsResponse {
  /** Retrieved items. */
  items?: Item[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface CancelScheduledItemRequest {
  /**
   * ID of the scheduled item to cancel.
   * @format GUID
   */
  _id: string;
}

export interface CancelScheduledItemResponse {
  /** Canceled item. */
  item?: Item;
}

export interface UpdateItemRequest {
  /** Item to update. */
  item: Item;
  /** @internal */
  mask?: string[];
}

export interface UpdateItemResponse {
  /** Updated item. */
  item?: Item;
}

export interface RescheduleItemRequest {
  /**
   * ID of the scheduled item to reschedule.
   * @format GUID
   */
  _id: string | null;
  /** Date and time to publish the item. If omitted, the item is published immediately. */
  scheduledDate?: Date | null;
}

export interface RescheduleItemResponse {
  /** Rescheduled item. */
  item?: Item;
}

/** DeleteItemRequest */
export interface DeleteItemRequest {
  /** @format GUID */
  _id: string;
}

/** DeleteItemResponse */
export interface DeleteItemResponse {}

export interface BulkPublishItemsRequest {
  /**
   * Items to publish.
   * @minSize 1
   * @maxSize 20
   */
  items: Item[];
  /** Policy that determines how items are published when one of them fails. */
  policy?: BulkPublishItemsPolicyWithLiterals;
  /** Scheduling info applied to all items. If set, the items are scheduled instead of published immediately. */
  schedulingInfo?: SchedulingInfo;
  itemExtras?: ItemExtras;
  /**
   * Whether to return the published items in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/** Policy that determines how items are published when one of them fails. */
export enum BulkPublishItemsPolicy {
  /** Publish all items, regardless of whether individual items fail. */
  CONCURRENT = 'CONCURRENT',
  /** Stop publishing remaining items as soon as one item fails. */
  SEQUENTIAL_STOP_ON_FAIL = 'SEQUENTIAL_STOP_ON_FAIL',
}

/** @enumType */
export type BulkPublishItemsPolicyWithLiterals =
  | BulkPublishItemsPolicy
  | 'CONCURRENT'
  | 'SEQUENTIAL_STOP_ON_FAIL';

export interface BulkPublishItemsResponse {
  /**
   * Results of the bulk publish, one per item, in the order they were sent.
   * @minSize 1
   * @maxSize 20
   */
  results?: BulkItemsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type PublishItemApplicationErrors =
  | {
      code?: 'REFERENCE_ID_ALREADY_EXIST';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INELIGIBLE_FOR_FEATURE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PUBLISH_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type PublishItemByIdApplicationErrors =
  | {
      code?: 'REFERENCE_ID_ALREADY_EXIST';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INELIGIBLE_FOR_FEATURE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PUBLISH_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MULTI_STORY_PUBLISH_NOT_SUPPORTED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type PublishMultiStoryDraftApplicationErrors =
  | {
      code?: 'NOT_MULTI_STORY_DRAFT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INELIGIBLE_FOR_FEATURE';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CancelScheduledItemApplicationErrors =
  | {
      code?: 'SCHEDULED_ITEM_ALREADY_CANCELED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SCHEDULED_ITEM_ALREADY_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SCHEDULED_ITEM_ALREADY_DELETED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateItemApplicationErrors =
  | {
      code?: 'ITEM_NOT_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SCHEDULED_ITEM_DATE_CHANGED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type RescheduleItemApplicationErrors =
  | {
      code?: 'ITEM_NOT_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ITEM_IS_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ITEM_IS_DELETED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INELIGIBLE_FOR_FEATURE';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type DeleteItemApplicationErrors =
  | {
      code?: 'ITEM_IS_DELETED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ITEM_NOT_EXISTS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkPublishItemsApplicationErrors = {
  code?: 'INELIGIBLE_FOR_FEATURE';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ItemPublishFailedEnvelope {
  data: ItemPublishFailed;
  metadata: EventMetadata;
}

/** @permissionScope Manage Social Posts
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SOCIAL-POSTS
 * @permissionId PROMOTE.PUBLISHER_EVENTS_READ
 * @webhook
 * @eventType wix.promote.publisher.v1.item_item_publish_failed
 * @serviceIdentifier com.wixpress.promote.publisher.v1.PublicationService
 * @slug item_publish_failed
 * @documentationMaturity preview
 */
export declare function onItemPublishFailed(
  handler: (event: ItemPublishFailedEnvelope) => void | Promise<void>
): void;

export interface ItemPublishScheduledEnvelope {
  data: ItemPublishScheduled;
  metadata: EventMetadata;
}

/** @permissionScope Manage Social Posts
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SOCIAL-POSTS
 * @permissionId PROMOTE.PUBLISHER_EVENTS_READ
 * @webhook
 * @eventType wix.promote.publisher.v1.item_item_publish_scheduled
 * @serviceIdentifier com.wixpress.promote.publisher.v1.PublicationService
 * @slug item_publish_scheduled
 * @documentationMaturity preview
 */
export declare function onItemPublishScheduled(
  handler: (event: ItemPublishScheduledEnvelope) => void | Promise<void>
): void;

export interface ItemPublishStartedEnvelope {
  data: ItemPublishStarted;
  metadata: EventMetadata;
}

/** @permissionScope Manage Social Posts
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SOCIAL-POSTS
 * @permissionId PROMOTE.PUBLISHER_EVENTS_READ
 * @webhook
 * @eventType wix.promote.publisher.v1.item_item_publish_started
 * @serviceIdentifier com.wixpress.promote.publisher.v1.PublicationService
 * @slug item_publish_started
 * @documentationMaturity preview
 */
export declare function onItemPublishStarted(
  handler: (event: ItemPublishStartedEnvelope) => void | Promise<void>
): void;

export interface ItemPublishedEnvelope {
  data: ItemPublished;
  metadata: EventMetadata;
}

/** @permissionScope Manage Social Posts
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SOCIAL-POSTS
 * @permissionId PROMOTE.PUBLISHER_EVENTS_READ
 * @webhook
 * @eventType wix.promote.publisher.v1.item_item_published
 * @serviceIdentifier com.wixpress.promote.publisher.v1.PublicationService
 * @slug item_published
 * @documentationMaturity preview
 */
export declare function onItemPublished(
  handler: (event: ItemPublishedEnvelope) => void | Promise<void>
): void;

/**
 * Publishes an item to a connected social channel.
 *
 * Use this method to publish a fully-formed post in a single call. To publish a previously created draft, call Publish Item By ID. To save a post without publishing it, call Create Draft Item. To schedule the post, set `item.schedulingInfo.scheduledDate`; otherwise it's published immediately.
 *
 * Published items appear on the site's Social Media Marketing page in the dashboard.
 * @public
 * @documentationMaturity preview
 * @requiredField options.item.channel
 * @requiredField options.item.channel.accountId
 * @permissionId PROMOTE.PUBLISHER_ITEM_CREATE
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.PublishItem
 */
export async function publishItem(
  options?: NonNullablePaths<
    PublishItemOptions,
    `item.channel` | `item.channel.accountId`,
    4
  >
): Promise<
  NonNullablePaths<
    PublishItemResponse,
    | `item.instagramPost.mediaWrapper.media`
    | `item.instagramPost.mediaWrapper.media.${number}.type`
    | `item.instagramPost.insights.likes`
    | `item.instagramPost.insights.engagement`
    | `item.instagramPost.insights.impressions`
    | `item.instagramPost.insights.plays`
    | `item.instagramPost.insights.reach`
    | `item.instagramPost.insights.comments`
    | `item.instagramPost.insights.shares`
    | `item.instagramPost.insights.saved`
    | `item.instagramPost.insights.totalInteractions`
    | `item.instagramStory.insights.impressions`
    | `item.instagramStory.insights.reach`
    | `item.instagramStory.insights.replies`
    | `item.facebookPost.insights.impressions.unique`
    | `item.facebookPost.insights.impressions.total`
    | `item.facebookPost.insights.interactions.unique`
    | `item.facebookPost.insights.interactions.total`
    | `item.facebookPost.insights.reactions.like`
    | `item.facebookPost.insights.reactions.love`
    | `item.facebookPost.insights.reactions.wow`
    | `item.facebookPost.insights.reactions.haha`
    | `item.facebookPost.insights.reactions.sad`
    | `item.facebookPost.insights.reactions.anger`
    | `item.facebookPost.insights.activity.share`
    | `item.facebookPost.insights.activity.like`
    | `item.facebookPost.insights.activity.comment`
    | `item.youtubeVideo.notifySubscribers`
    | `item.youtubeVideo.insights.views`
    | `item.youtubeVideo.insights.comments`
    | `item.youtubeVideo.insights.likes`
    | `item.youtubeVideo.insights.dislikes`
    | `item.youtubeVideo.insights.shares`
    | `item.youtubeShort.notifySubscribers`
    | `item.linkedinPost.insights.uniqueImpressions`
    | `item.linkedinPost.insights.impression`
    | `item.linkedinPost.insights.shares`
    | `item.linkedinPost.insights.engagement`
    | `item.linkedinPost.insights.clicks`
    | `item.linkedinPost.insights.likes`
    | `item.linkedinPost.insights.comments`
    | `item.twitterPost.insights.retweets`
    | `item.twitterPost.insights.replies`
    | `item.twitterPost.insights.likes`
    | `item.twitterPost.insights.quotes`
    | `item.twitterPost.insights.bookmarks`
    | `item.twitterPost.insights.impressions`
    | `item.pinterestPost.insights.impressions`
    | `item.pinterestPost.insights.clicks`
    | `item.pinterestPost.insights.saves`
    | `item.pinterestPost.insights.comments`
    | `item.pinterestPost.insights.reactions`
    | `item.gbpPost.callToAction.actionType`
    | `item.gbpPost.insights.shares`
    | `item.tiktokVideo.privacyLevel`
    | `item.tiktokVideo.disableComment`
    | `item.tiktokVideo.disableDuet`
    | `item.tiktokVideo.disableStitch`
    | `item.tiktokVideo.brandContentToggle`
    | `item.tiktokVideo.brandOrganicToggle`
    | `item.tiktokVideo.insights.views`
    | `item.tiktokVideo.insights.likes`
    | `item.tiktokVideo.insights.comments`
    | `item.tiktokVideo.insights.shares`
    | `item.tiktokPhoto.privacyLevel`
    | `item.tiktokPhoto.disableComment`
    | `item.tiktokPhoto.autoAddMusic`
    | `item.tiktokPhoto.brandContentToggle`
    | `item.tiktokPhoto.brandOrganicToggle`
    | `item.channel.name`
    | `item.type`
    | `item.status`
    | `item.siteAnalytics.traffic`
    | `item.siteAnalytics.pageView`
    | `item.siteAnalytics.siteBounceRate`
    | `item.siteAnalytics.revenue`
    | `item.siteAnalytics.conversionFunnel.cartViewsSessions`
    | `item.siteAnalytics.conversionFunnel.checkOutSessions`
    | `item.siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `item.siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `item.insightsSummary.impressions`
    | `item.insightsSummary.interactions`
    | `item.insightsSummary.impressionsTotal`
    | `item.suggested`
    | `item.external`
    | `item.siteAssetRef.siteAssetRefType`
    | `item.siteAssetRef.name`,
    7
  > & {
    __applicationErrorsType?: PublishItemApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    item: options?.item,
    itemExtras: options?.itemExtras,
  });

  const reqOpts = ambassadorWixPromotePublisherV1Item.publishItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          item: '$[0].item',
          itemExtras: '$[0].itemExtras',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PublishItemOptions {
  /** Item to publish. */
  item?: Item;
  itemExtras?: ItemExtras;
}

/**
 * Publishes a previously created item by its `id`.
 *
 * The item can be a draft or a scheduled item. To schedule the item for a future date, set `scheduledDate`; otherwise it's published immediately.
 *
 * Published items appear on the site's Social Media Marketing page in the dashboard.
 * @param _id - ID of the item to publish.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId PROMOTE.PUBLISHER_ITEM_CREATE
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.PublishItemById
 */
export async function publishItemById(
  _id: string,
  options?: PublishItemByIdOptions
): Promise<
  NonNullablePaths<
    PublishItemByIdResponse,
    | `item.instagramPost.mediaWrapper.media`
    | `item.instagramPost.mediaWrapper.media.${number}.type`
    | `item.instagramPost.insights.likes`
    | `item.instagramPost.insights.engagement`
    | `item.instagramPost.insights.impressions`
    | `item.instagramPost.insights.plays`
    | `item.instagramPost.insights.reach`
    | `item.instagramPost.insights.comments`
    | `item.instagramPost.insights.shares`
    | `item.instagramPost.insights.saved`
    | `item.instagramPost.insights.totalInteractions`
    | `item.instagramStory.insights.impressions`
    | `item.instagramStory.insights.reach`
    | `item.instagramStory.insights.replies`
    | `item.facebookPost.insights.impressions.unique`
    | `item.facebookPost.insights.impressions.total`
    | `item.facebookPost.insights.interactions.unique`
    | `item.facebookPost.insights.interactions.total`
    | `item.facebookPost.insights.reactions.like`
    | `item.facebookPost.insights.reactions.love`
    | `item.facebookPost.insights.reactions.wow`
    | `item.facebookPost.insights.reactions.haha`
    | `item.facebookPost.insights.reactions.sad`
    | `item.facebookPost.insights.reactions.anger`
    | `item.facebookPost.insights.activity.share`
    | `item.facebookPost.insights.activity.like`
    | `item.facebookPost.insights.activity.comment`
    | `item.youtubeVideo.notifySubscribers`
    | `item.youtubeVideo.insights.views`
    | `item.youtubeVideo.insights.comments`
    | `item.youtubeVideo.insights.likes`
    | `item.youtubeVideo.insights.dislikes`
    | `item.youtubeVideo.insights.shares`
    | `item.youtubeShort.notifySubscribers`
    | `item.linkedinPost.insights.uniqueImpressions`
    | `item.linkedinPost.insights.impression`
    | `item.linkedinPost.insights.shares`
    | `item.linkedinPost.insights.engagement`
    | `item.linkedinPost.insights.clicks`
    | `item.linkedinPost.insights.likes`
    | `item.linkedinPost.insights.comments`
    | `item.twitterPost.insights.retweets`
    | `item.twitterPost.insights.replies`
    | `item.twitterPost.insights.likes`
    | `item.twitterPost.insights.quotes`
    | `item.twitterPost.insights.bookmarks`
    | `item.twitterPost.insights.impressions`
    | `item.pinterestPost.insights.impressions`
    | `item.pinterestPost.insights.clicks`
    | `item.pinterestPost.insights.saves`
    | `item.pinterestPost.insights.comments`
    | `item.pinterestPost.insights.reactions`
    | `item.gbpPost.callToAction.actionType`
    | `item.gbpPost.insights.shares`
    | `item.tiktokVideo.privacyLevel`
    | `item.tiktokVideo.disableComment`
    | `item.tiktokVideo.disableDuet`
    | `item.tiktokVideo.disableStitch`
    | `item.tiktokVideo.brandContentToggle`
    | `item.tiktokVideo.brandOrganicToggle`
    | `item.tiktokVideo.insights.views`
    | `item.tiktokVideo.insights.likes`
    | `item.tiktokVideo.insights.comments`
    | `item.tiktokVideo.insights.shares`
    | `item.tiktokPhoto.privacyLevel`
    | `item.tiktokPhoto.disableComment`
    | `item.tiktokPhoto.autoAddMusic`
    | `item.tiktokPhoto.brandContentToggle`
    | `item.tiktokPhoto.brandOrganicToggle`
    | `item.channel.name`
    | `item.type`
    | `item.status`
    | `item.siteAnalytics.traffic`
    | `item.siteAnalytics.pageView`
    | `item.siteAnalytics.siteBounceRate`
    | `item.siteAnalytics.revenue`
    | `item.siteAnalytics.conversionFunnel.cartViewsSessions`
    | `item.siteAnalytics.conversionFunnel.checkOutSessions`
    | `item.siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `item.siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `item.insightsSummary.impressions`
    | `item.insightsSummary.interactions`
    | `item.insightsSummary.impressionsTotal`
    | `item.suggested`
    | `item.external`
    | `item.siteAssetRef.siteAssetRefType`
    | `item.siteAssetRef.name`,
    7
  > & {
    __applicationErrorsType?: PublishItemByIdApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    scheduledDate: options?.scheduledDate,
  });

  const reqOpts = ambassadorWixPromotePublisherV1Item.publishItemById(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          scheduledDate: '$[1].scheduledDate',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PublishItemByIdOptions {
  /** Date and time to publish the item. If omitted, the item is published immediately. */
  scheduledDate?: Date | null;
}

/**
 * Creates a draft item.
 *
 * A draft is an item that's saved but not yet published, with a `status` of `DRAFT`. Drafts appear on the site's Social Media Marketing page in the dashboard, where they can be reviewed and published. To publish a draft, call Publish Item By ID.
 *
 * The item's `type` and content must match a combination supported by the target channel. See `item.type` for the supported combinations.
 * @public
 * @documentationMaturity preview
 * @requiredField options.item.channel
 * @permissionId PROMOTE.PUBLISHER_ITEM_CREATE
 * @applicableIdentity APP
 * @returns Created draft item.
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.CreateDraftItem
 */
export async function createDraftItem(
  options?: NonNullablePaths<CreateDraftItemOptions, `item.channel`, 3>
): Promise<
  NonNullablePaths<
    Item,
    | `instagramPost.mediaWrapper.media`
    | `instagramPost.mediaWrapper.media.${number}.type`
    | `instagramPost.insights.likes`
    | `instagramPost.insights.engagement`
    | `instagramPost.insights.impressions`
    | `instagramPost.insights.plays`
    | `instagramPost.insights.reach`
    | `instagramPost.insights.comments`
    | `instagramPost.insights.shares`
    | `instagramPost.insights.saved`
    | `instagramPost.insights.totalInteractions`
    | `instagramStory.insights.impressions`
    | `instagramStory.insights.reach`
    | `instagramStory.insights.replies`
    | `facebookPost.insights.impressions.unique`
    | `facebookPost.insights.impressions.total`
    | `facebookPost.insights.interactions.unique`
    | `facebookPost.insights.interactions.total`
    | `facebookPost.insights.reactions.like`
    | `facebookPost.insights.reactions.love`
    | `facebookPost.insights.reactions.wow`
    | `facebookPost.insights.reactions.haha`
    | `facebookPost.insights.reactions.sad`
    | `facebookPost.insights.reactions.anger`
    | `facebookPost.insights.activity.share`
    | `facebookPost.insights.activity.like`
    | `facebookPost.insights.activity.comment`
    | `youtubeVideo.notifySubscribers`
    | `youtubeVideo.insights.views`
    | `youtubeVideo.insights.comments`
    | `youtubeVideo.insights.likes`
    | `youtubeVideo.insights.dislikes`
    | `youtubeVideo.insights.shares`
    | `youtubeShort.notifySubscribers`
    | `linkedinPost.insights.uniqueImpressions`
    | `linkedinPost.insights.impression`
    | `linkedinPost.insights.shares`
    | `linkedinPost.insights.engagement`
    | `linkedinPost.insights.clicks`
    | `linkedinPost.insights.likes`
    | `linkedinPost.insights.comments`
    | `twitterPost.insights.retweets`
    | `twitterPost.insights.replies`
    | `twitterPost.insights.likes`
    | `twitterPost.insights.quotes`
    | `twitterPost.insights.bookmarks`
    | `twitterPost.insights.impressions`
    | `pinterestPost.insights.impressions`
    | `pinterestPost.insights.clicks`
    | `pinterestPost.insights.saves`
    | `pinterestPost.insights.comments`
    | `pinterestPost.insights.reactions`
    | `gbpPost.callToAction.actionType`
    | `gbpPost.insights.shares`
    | `tiktokVideo.privacyLevel`
    | `tiktokVideo.disableComment`
    | `tiktokVideo.disableDuet`
    | `tiktokVideo.disableStitch`
    | `tiktokVideo.brandContentToggle`
    | `tiktokVideo.brandOrganicToggle`
    | `tiktokVideo.insights.views`
    | `tiktokVideo.insights.likes`
    | `tiktokVideo.insights.comments`
    | `tiktokVideo.insights.shares`
    | `tiktokPhoto.privacyLevel`
    | `tiktokPhoto.disableComment`
    | `tiktokPhoto.autoAddMusic`
    | `tiktokPhoto.brandContentToggle`
    | `tiktokPhoto.brandOrganicToggle`
    | `channel.name`
    | `type`
    | `status`
    | `siteAnalytics.traffic`
    | `siteAnalytics.pageView`
    | `siteAnalytics.siteBounceRate`
    | `siteAnalytics.revenue`
    | `siteAnalytics.conversionFunnel.cartViewsSessions`
    | `siteAnalytics.conversionFunnel.checkOutSessions`
    | `siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `insightsSummary.impressions`
    | `insightsSummary.interactions`
    | `insightsSummary.impressionsTotal`
    | `suggested`
    | `external`
    | `siteAssetRef.siteAssetRefType`
    | `siteAssetRef.name`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    item: options?.item,
    itemExtras: options?.itemExtras,
  });

  const reqOpts = ambassadorWixPromotePublisherV1Item.createDraftItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          item: '$[0].item',
          itemExtras: '$[0].itemExtras',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateDraftItemOptions {
  /** Item to create as a draft. */
  item?: Item;
  itemExtras?: ItemExtras;
}

/**
 * Publishes a multi-story draft as a sequence of single-media story items.
 *
 * The draft's media list is split into individual story items that are published sequentially
 * in media order, stopping on the first failure. Each story item gets a derived `referenceId`
 * of `{draftReferenceId}:story:{index}` (the draft `id` is used when the draft has no `referenceId`).
 * Story items already published by a previous call are skipped, so the method can be safely retried
 * after a partial failure. The draft is deleted only after all story items are published.
 * To schedule the story sequence, set `schedulingInfo.scheduledDate`; otherwise it's published immediately.
 * @param _id - ID of the multi-story draft to publish.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId PROMOTE.PUBLISHER_ITEM_CREATE
 * @applicableIdentity APP
 * @returns PublishMultiStoryDraftResponse
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.PublishMultiStoryDraft
 */
export async function publishMultiStoryDraft(
  _id: string,
  options?: PublishMultiStoryDraftOptions
): Promise<
  NonNullablePaths<
    PublishMultiStoryDraftResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.youtubeVideo.notifySubscribers`
    | `results.${number}.item.youtubeShort.notifySubscribers`
    | `results.${number}.item.tiktokVideo.privacyLevel`
    | `results.${number}.item.tiktokVideo.disableComment`
    | `results.${number}.item.tiktokVideo.disableDuet`
    | `results.${number}.item.tiktokVideo.disableStitch`
    | `results.${number}.item.tiktokVideo.brandContentToggle`
    | `results.${number}.item.tiktokVideo.brandOrganicToggle`
    | `results.${number}.item.tiktokPhoto.privacyLevel`
    | `results.${number}.item.tiktokPhoto.disableComment`
    | `results.${number}.item.tiktokPhoto.autoAddMusic`
    | `results.${number}.item.tiktokPhoto.brandContentToggle`
    | `results.${number}.item.tiktokPhoto.brandOrganicToggle`
    | `results.${number}.item.channel.name`
    | `results.${number}.item.type`
    | `results.${number}.item.status`
    | `results.${number}.item.siteAnalytics.traffic`
    | `results.${number}.item.siteAnalytics.pageView`
    | `results.${number}.item.siteAnalytics.siteBounceRate`
    | `results.${number}.item.siteAnalytics.revenue`
    | `results.${number}.item.insightsSummary.impressions`
    | `results.${number}.item.insightsSummary.interactions`
    | `results.${number}.item.insightsSummary.impressionsTotal`
    | `results.${number}.item.suggested`
    | `results.${number}.item.external`
    | `results.${number}.item.siteAssetRef.siteAssetRefType`
    | `results.${number}.item.siteAssetRef.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: PublishMultiStoryDraftApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    schedulingInfo: options?.schedulingInfo,
  });

  const reqOpts =
    ambassadorWixPromotePublisherV1Item.publishMultiStoryDraft(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          schedulingInfo: '$[1].schedulingInfo',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PublishMultiStoryDraftOptions {
  /** Scheduling info applied to all story items. If set, the story items are scheduled instead of published immediately. */
  schedulingInfo?: SchedulingInfo;
}

/**
 * Retrieves an item by ID.
 * @param _id - ID of the item to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId PROMOTE.PUBLISHER_ITEM_READ
 * @applicableIdentity APP
 * @returns Retrieved item.
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.GetItem
 */
export async function getItem(
  _id: string
): Promise<
  NonNullablePaths<
    Item,
    | `instagramPost.mediaWrapper.media`
    | `instagramPost.mediaWrapper.media.${number}.type`
    | `instagramPost.insights.likes`
    | `instagramPost.insights.engagement`
    | `instagramPost.insights.impressions`
    | `instagramPost.insights.plays`
    | `instagramPost.insights.reach`
    | `instagramPost.insights.comments`
    | `instagramPost.insights.shares`
    | `instagramPost.insights.saved`
    | `instagramPost.insights.totalInteractions`
    | `instagramStory.insights.impressions`
    | `instagramStory.insights.reach`
    | `instagramStory.insights.replies`
    | `facebookPost.insights.impressions.unique`
    | `facebookPost.insights.impressions.total`
    | `facebookPost.insights.interactions.unique`
    | `facebookPost.insights.interactions.total`
    | `facebookPost.insights.reactions.like`
    | `facebookPost.insights.reactions.love`
    | `facebookPost.insights.reactions.wow`
    | `facebookPost.insights.reactions.haha`
    | `facebookPost.insights.reactions.sad`
    | `facebookPost.insights.reactions.anger`
    | `facebookPost.insights.activity.share`
    | `facebookPost.insights.activity.like`
    | `facebookPost.insights.activity.comment`
    | `youtubeVideo.notifySubscribers`
    | `youtubeVideo.insights.views`
    | `youtubeVideo.insights.comments`
    | `youtubeVideo.insights.likes`
    | `youtubeVideo.insights.dislikes`
    | `youtubeVideo.insights.shares`
    | `youtubeShort.notifySubscribers`
    | `linkedinPost.insights.uniqueImpressions`
    | `linkedinPost.insights.impression`
    | `linkedinPost.insights.shares`
    | `linkedinPost.insights.engagement`
    | `linkedinPost.insights.clicks`
    | `linkedinPost.insights.likes`
    | `linkedinPost.insights.comments`
    | `twitterPost.insights.retweets`
    | `twitterPost.insights.replies`
    | `twitterPost.insights.likes`
    | `twitterPost.insights.quotes`
    | `twitterPost.insights.bookmarks`
    | `twitterPost.insights.impressions`
    | `pinterestPost.insights.impressions`
    | `pinterestPost.insights.clicks`
    | `pinterestPost.insights.saves`
    | `pinterestPost.insights.comments`
    | `pinterestPost.insights.reactions`
    | `gbpPost.callToAction.actionType`
    | `gbpPost.insights.shares`
    | `tiktokVideo.privacyLevel`
    | `tiktokVideo.disableComment`
    | `tiktokVideo.disableDuet`
    | `tiktokVideo.disableStitch`
    | `tiktokVideo.brandContentToggle`
    | `tiktokVideo.brandOrganicToggle`
    | `tiktokVideo.insights.views`
    | `tiktokVideo.insights.likes`
    | `tiktokVideo.insights.comments`
    | `tiktokVideo.insights.shares`
    | `tiktokPhoto.privacyLevel`
    | `tiktokPhoto.disableComment`
    | `tiktokPhoto.autoAddMusic`
    | `tiktokPhoto.brandContentToggle`
    | `tiktokPhoto.brandOrganicToggle`
    | `channel.name`
    | `type`
    | `status`
    | `siteAnalytics.traffic`
    | `siteAnalytics.pageView`
    | `siteAnalytics.siteBounceRate`
    | `siteAnalytics.revenue`
    | `siteAnalytics.conversionFunnel.cartViewsSessions`
    | `siteAnalytics.conversionFunnel.checkOutSessions`
    | `siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `insightsSummary.impressions`
    | `insightsSummary.interactions`
    | `insightsSummary.impressionsTotal`
    | `suggested`
    | `external`
    | `siteAssetRef.siteAssetRefType`
    | `siteAssetRef.name`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts = ambassadorWixPromotePublisherV1Item.getItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of items by their IDs.
 * @param ids - IDs of the items to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField ids
 * @permissionId PROMOTE.PUBLISHER_ITEM_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.GetItems
 */
export async function getItems(
  ids: string[]
): Promise<
  NonNullablePaths<
    GetItemsResponse,
    | `items`
    | `items.${number}.instagramPost.insights.likes`
    | `items.${number}.instagramPost.insights.engagement`
    | `items.${number}.instagramPost.insights.impressions`
    | `items.${number}.instagramPost.insights.plays`
    | `items.${number}.instagramPost.insights.reach`
    | `items.${number}.instagramPost.insights.comments`
    | `items.${number}.instagramPost.insights.shares`
    | `items.${number}.instagramPost.insights.saved`
    | `items.${number}.instagramPost.insights.totalInteractions`
    | `items.${number}.instagramStory.insights.impressions`
    | `items.${number}.instagramStory.insights.reach`
    | `items.${number}.instagramStory.insights.replies`
    | `items.${number}.youtubeVideo.notifySubscribers`
    | `items.${number}.youtubeVideo.insights.views`
    | `items.${number}.youtubeVideo.insights.comments`
    | `items.${number}.youtubeVideo.insights.likes`
    | `items.${number}.youtubeVideo.insights.dislikes`
    | `items.${number}.youtubeVideo.insights.shares`
    | `items.${number}.youtubeShort.notifySubscribers`
    | `items.${number}.linkedinPost.insights.uniqueImpressions`
    | `items.${number}.linkedinPost.insights.impression`
    | `items.${number}.linkedinPost.insights.shares`
    | `items.${number}.linkedinPost.insights.engagement`
    | `items.${number}.linkedinPost.insights.clicks`
    | `items.${number}.linkedinPost.insights.likes`
    | `items.${number}.linkedinPost.insights.comments`
    | `items.${number}.twitterPost.insights.retweets`
    | `items.${number}.twitterPost.insights.replies`
    | `items.${number}.twitterPost.insights.likes`
    | `items.${number}.twitterPost.insights.quotes`
    | `items.${number}.twitterPost.insights.bookmarks`
    | `items.${number}.twitterPost.insights.impressions`
    | `items.${number}.pinterestPost.insights.impressions`
    | `items.${number}.pinterestPost.insights.clicks`
    | `items.${number}.pinterestPost.insights.saves`
    | `items.${number}.pinterestPost.insights.comments`
    | `items.${number}.pinterestPost.insights.reactions`
    | `items.${number}.gbpPost.callToAction.actionType`
    | `items.${number}.gbpPost.insights.shares`
    | `items.${number}.tiktokVideo.privacyLevel`
    | `items.${number}.tiktokVideo.disableComment`
    | `items.${number}.tiktokVideo.disableDuet`
    | `items.${number}.tiktokVideo.disableStitch`
    | `items.${number}.tiktokVideo.brandContentToggle`
    | `items.${number}.tiktokVideo.brandOrganicToggle`
    | `items.${number}.tiktokVideo.insights.views`
    | `items.${number}.tiktokVideo.insights.likes`
    | `items.${number}.tiktokVideo.insights.comments`
    | `items.${number}.tiktokVideo.insights.shares`
    | `items.${number}.tiktokPhoto.privacyLevel`
    | `items.${number}.tiktokPhoto.disableComment`
    | `items.${number}.tiktokPhoto.autoAddMusic`
    | `items.${number}.tiktokPhoto.brandContentToggle`
    | `items.${number}.tiktokPhoto.brandOrganicToggle`
    | `items.${number}.channel.name`
    | `items.${number}.type`
    | `items.${number}.status`
    | `items.${number}.siteAnalytics.traffic`
    | `items.${number}.siteAnalytics.pageView`
    | `items.${number}.siteAnalytics.siteBounceRate`
    | `items.${number}.siteAnalytics.revenue`
    | `items.${number}.siteAnalytics.conversionFunnel.cartViewsSessions`
    | `items.${number}.siteAnalytics.conversionFunnel.checkOutSessions`
    | `items.${number}.siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `items.${number}.siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `items.${number}.insightsSummary.impressions`
    | `items.${number}.insightsSummary.interactions`
    | `items.${number}.insightsSummary.impressionsTotal`
    | `items.${number}.suggested`
    | `items.${number}.external`
    | `items.${number}.siteAssetRef.siteAssetRefType`
    | `items.${number}.siteAssetRef.name`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts = ambassadorWixPromotePublisherV1Item.getItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of items, given the provided paging, filtering, and sorting.
 *
 * Query Items runs with these defaults, which you can override:
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 * @param query - Query options, including paging, filtering, and sorting.
 * @public
 * @documentationMaturity preview
 * @requiredField query
 * @permissionId PROMOTE.PUBLISHER_ITEM_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.QueryItems
 */
export async function queryItems(
  query: Query
): Promise<
  NonNullablePaths<
    QueryItemsResponse,
    | `items`
    | `items.${number}.instagramPost.insights.likes`
    | `items.${number}.instagramPost.insights.engagement`
    | `items.${number}.instagramPost.insights.impressions`
    | `items.${number}.instagramPost.insights.plays`
    | `items.${number}.instagramPost.insights.reach`
    | `items.${number}.instagramPost.insights.comments`
    | `items.${number}.instagramPost.insights.shares`
    | `items.${number}.instagramPost.insights.saved`
    | `items.${number}.instagramPost.insights.totalInteractions`
    | `items.${number}.instagramStory.insights.impressions`
    | `items.${number}.instagramStory.insights.reach`
    | `items.${number}.instagramStory.insights.replies`
    | `items.${number}.youtubeVideo.notifySubscribers`
    | `items.${number}.youtubeVideo.insights.views`
    | `items.${number}.youtubeVideo.insights.comments`
    | `items.${number}.youtubeVideo.insights.likes`
    | `items.${number}.youtubeVideo.insights.dislikes`
    | `items.${number}.youtubeVideo.insights.shares`
    | `items.${number}.youtubeShort.notifySubscribers`
    | `items.${number}.linkedinPost.insights.uniqueImpressions`
    | `items.${number}.linkedinPost.insights.impression`
    | `items.${number}.linkedinPost.insights.shares`
    | `items.${number}.linkedinPost.insights.engagement`
    | `items.${number}.linkedinPost.insights.clicks`
    | `items.${number}.linkedinPost.insights.likes`
    | `items.${number}.linkedinPost.insights.comments`
    | `items.${number}.twitterPost.insights.retweets`
    | `items.${number}.twitterPost.insights.replies`
    | `items.${number}.twitterPost.insights.likes`
    | `items.${number}.twitterPost.insights.quotes`
    | `items.${number}.twitterPost.insights.bookmarks`
    | `items.${number}.twitterPost.insights.impressions`
    | `items.${number}.pinterestPost.insights.impressions`
    | `items.${number}.pinterestPost.insights.clicks`
    | `items.${number}.pinterestPost.insights.saves`
    | `items.${number}.pinterestPost.insights.comments`
    | `items.${number}.pinterestPost.insights.reactions`
    | `items.${number}.gbpPost.callToAction.actionType`
    | `items.${number}.gbpPost.insights.shares`
    | `items.${number}.tiktokVideo.privacyLevel`
    | `items.${number}.tiktokVideo.disableComment`
    | `items.${number}.tiktokVideo.disableDuet`
    | `items.${number}.tiktokVideo.disableStitch`
    | `items.${number}.tiktokVideo.brandContentToggle`
    | `items.${number}.tiktokVideo.brandOrganicToggle`
    | `items.${number}.tiktokVideo.insights.views`
    | `items.${number}.tiktokVideo.insights.likes`
    | `items.${number}.tiktokVideo.insights.comments`
    | `items.${number}.tiktokVideo.insights.shares`
    | `items.${number}.tiktokPhoto.privacyLevel`
    | `items.${number}.tiktokPhoto.disableComment`
    | `items.${number}.tiktokPhoto.autoAddMusic`
    | `items.${number}.tiktokPhoto.brandContentToggle`
    | `items.${number}.tiktokPhoto.brandOrganicToggle`
    | `items.${number}.channel.name`
    | `items.${number}.type`
    | `items.${number}.status`
    | `items.${number}.siteAnalytics.traffic`
    | `items.${number}.siteAnalytics.pageView`
    | `items.${number}.siteAnalytics.siteBounceRate`
    | `items.${number}.siteAnalytics.revenue`
    | `items.${number}.siteAnalytics.conversionFunnel.cartViewsSessions`
    | `items.${number}.siteAnalytics.conversionFunnel.checkOutSessions`
    | `items.${number}.siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `items.${number}.siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `items.${number}.insightsSummary.impressions`
    | `items.${number}.insightsSummary.interactions`
    | `items.${number}.insightsSummary.impressionsTotal`
    | `items.${number}.suggested`
    | `items.${number}.external`
    | `items.${number}.siteAssetRef.siteAssetRefType`
    | `items.${number}.siteAssetRef.name`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixPromotePublisherV1Item.queryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Cancels the scheduling of a scheduled item.
 *
 * The item is no longer published at its scheduled time. Only an item with a `status` of `SCHEDULED` can be canceled.
 * @param _id - ID of the scheduled item to cancel.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId PROMOTE.PUBLISHER_ITEM_CANCEL_SCHEDULED
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.CancelScheduledItem
 */
export async function cancelScheduledItem(_id: string): Promise<
  NonNullablePaths<
    CancelScheduledItemResponse,
    | `item.instagramPost.mediaWrapper.media`
    | `item.instagramPost.mediaWrapper.media.${number}.type`
    | `item.instagramPost.insights.likes`
    | `item.instagramPost.insights.engagement`
    | `item.instagramPost.insights.impressions`
    | `item.instagramPost.insights.plays`
    | `item.instagramPost.insights.reach`
    | `item.instagramPost.insights.comments`
    | `item.instagramPost.insights.shares`
    | `item.instagramPost.insights.saved`
    | `item.instagramPost.insights.totalInteractions`
    | `item.instagramStory.insights.impressions`
    | `item.instagramStory.insights.reach`
    | `item.instagramStory.insights.replies`
    | `item.facebookPost.insights.impressions.unique`
    | `item.facebookPost.insights.impressions.total`
    | `item.facebookPost.insights.interactions.unique`
    | `item.facebookPost.insights.interactions.total`
    | `item.facebookPost.insights.reactions.like`
    | `item.facebookPost.insights.reactions.love`
    | `item.facebookPost.insights.reactions.wow`
    | `item.facebookPost.insights.reactions.haha`
    | `item.facebookPost.insights.reactions.sad`
    | `item.facebookPost.insights.reactions.anger`
    | `item.facebookPost.insights.activity.share`
    | `item.facebookPost.insights.activity.like`
    | `item.facebookPost.insights.activity.comment`
    | `item.youtubeVideo.notifySubscribers`
    | `item.youtubeVideo.insights.views`
    | `item.youtubeVideo.insights.comments`
    | `item.youtubeVideo.insights.likes`
    | `item.youtubeVideo.insights.dislikes`
    | `item.youtubeVideo.insights.shares`
    | `item.youtubeShort.notifySubscribers`
    | `item.linkedinPost.insights.uniqueImpressions`
    | `item.linkedinPost.insights.impression`
    | `item.linkedinPost.insights.shares`
    | `item.linkedinPost.insights.engagement`
    | `item.linkedinPost.insights.clicks`
    | `item.linkedinPost.insights.likes`
    | `item.linkedinPost.insights.comments`
    | `item.twitterPost.insights.retweets`
    | `item.twitterPost.insights.replies`
    | `item.twitterPost.insights.likes`
    | `item.twitterPost.insights.quotes`
    | `item.twitterPost.insights.bookmarks`
    | `item.twitterPost.insights.impressions`
    | `item.pinterestPost.insights.impressions`
    | `item.pinterestPost.insights.clicks`
    | `item.pinterestPost.insights.saves`
    | `item.pinterestPost.insights.comments`
    | `item.pinterestPost.insights.reactions`
    | `item.gbpPost.callToAction.actionType`
    | `item.gbpPost.insights.shares`
    | `item.tiktokVideo.privacyLevel`
    | `item.tiktokVideo.disableComment`
    | `item.tiktokVideo.disableDuet`
    | `item.tiktokVideo.disableStitch`
    | `item.tiktokVideo.brandContentToggle`
    | `item.tiktokVideo.brandOrganicToggle`
    | `item.tiktokVideo.insights.views`
    | `item.tiktokVideo.insights.likes`
    | `item.tiktokVideo.insights.comments`
    | `item.tiktokVideo.insights.shares`
    | `item.tiktokPhoto.privacyLevel`
    | `item.tiktokPhoto.disableComment`
    | `item.tiktokPhoto.autoAddMusic`
    | `item.tiktokPhoto.brandContentToggle`
    | `item.tiktokPhoto.brandOrganicToggle`
    | `item.channel.name`
    | `item.type`
    | `item.status`
    | `item.siteAnalytics.traffic`
    | `item.siteAnalytics.pageView`
    | `item.siteAnalytics.siteBounceRate`
    | `item.siteAnalytics.revenue`
    | `item.siteAnalytics.conversionFunnel.cartViewsSessions`
    | `item.siteAnalytics.conversionFunnel.checkOutSessions`
    | `item.siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `item.siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `item.insightsSummary.impressions`
    | `item.insightsSummary.interactions`
    | `item.insightsSummary.impressionsTotal`
    | `item.suggested`
    | `item.external`
    | `item.siteAssetRef.siteAssetRefType`
    | `item.siteAssetRef.name`,
    7
  > & {
    __applicationErrorsType?: CancelScheduledItemApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts =
    ambassadorWixPromotePublisherV1Item.cancelScheduledItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates an item.
 *
 * Provide the `id` of the item to update along with the fields to change. Only the fields you include are changed. To change a scheduled item's publish date, call Reschedule Item instead.
 * @param _id - Item ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField item
 * @permissionId PROMOTE.PUBLISHER_ITEM_UPDATE
 * @applicableIdentity APP
 * @returns Updated item.
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.UpdateItem
 */
export async function updateItem(
  _id: string,
  item: UpdateItem
): Promise<
  NonNullablePaths<
    Item,
    | `instagramPost.mediaWrapper.media`
    | `instagramPost.mediaWrapper.media.${number}.type`
    | `instagramPost.insights.likes`
    | `instagramPost.insights.engagement`
    | `instagramPost.insights.impressions`
    | `instagramPost.insights.plays`
    | `instagramPost.insights.reach`
    | `instagramPost.insights.comments`
    | `instagramPost.insights.shares`
    | `instagramPost.insights.saved`
    | `instagramPost.insights.totalInteractions`
    | `instagramStory.insights.impressions`
    | `instagramStory.insights.reach`
    | `instagramStory.insights.replies`
    | `facebookPost.insights.impressions.unique`
    | `facebookPost.insights.impressions.total`
    | `facebookPost.insights.interactions.unique`
    | `facebookPost.insights.interactions.total`
    | `facebookPost.insights.reactions.like`
    | `facebookPost.insights.reactions.love`
    | `facebookPost.insights.reactions.wow`
    | `facebookPost.insights.reactions.haha`
    | `facebookPost.insights.reactions.sad`
    | `facebookPost.insights.reactions.anger`
    | `facebookPost.insights.activity.share`
    | `facebookPost.insights.activity.like`
    | `facebookPost.insights.activity.comment`
    | `youtubeVideo.notifySubscribers`
    | `youtubeVideo.insights.views`
    | `youtubeVideo.insights.comments`
    | `youtubeVideo.insights.likes`
    | `youtubeVideo.insights.dislikes`
    | `youtubeVideo.insights.shares`
    | `youtubeShort.notifySubscribers`
    | `linkedinPost.insights.uniqueImpressions`
    | `linkedinPost.insights.impression`
    | `linkedinPost.insights.shares`
    | `linkedinPost.insights.engagement`
    | `linkedinPost.insights.clicks`
    | `linkedinPost.insights.likes`
    | `linkedinPost.insights.comments`
    | `twitterPost.insights.retweets`
    | `twitterPost.insights.replies`
    | `twitterPost.insights.likes`
    | `twitterPost.insights.quotes`
    | `twitterPost.insights.bookmarks`
    | `twitterPost.insights.impressions`
    | `pinterestPost.insights.impressions`
    | `pinterestPost.insights.clicks`
    | `pinterestPost.insights.saves`
    | `pinterestPost.insights.comments`
    | `pinterestPost.insights.reactions`
    | `gbpPost.callToAction.actionType`
    | `gbpPost.insights.shares`
    | `tiktokVideo.privacyLevel`
    | `tiktokVideo.disableComment`
    | `tiktokVideo.disableDuet`
    | `tiktokVideo.disableStitch`
    | `tiktokVideo.brandContentToggle`
    | `tiktokVideo.brandOrganicToggle`
    | `tiktokVideo.insights.views`
    | `tiktokVideo.insights.likes`
    | `tiktokVideo.insights.comments`
    | `tiktokVideo.insights.shares`
    | `tiktokPhoto.privacyLevel`
    | `tiktokPhoto.disableComment`
    | `tiktokPhoto.autoAddMusic`
    | `tiktokPhoto.brandContentToggle`
    | `tiktokPhoto.brandOrganicToggle`
    | `channel.name`
    | `type`
    | `status`
    | `siteAnalytics.traffic`
    | `siteAnalytics.pageView`
    | `siteAnalytics.siteBounceRate`
    | `siteAnalytics.revenue`
    | `siteAnalytics.conversionFunnel.cartViewsSessions`
    | `siteAnalytics.conversionFunnel.checkOutSessions`
    | `siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `insightsSummary.impressions`
    | `insightsSummary.interactions`
    | `insightsSummary.impressionsTotal`
    | `suggested`
    | `external`
    | `siteAssetRef.siteAssetRefType`
    | `siteAssetRef.name`,
    6
  > & {
    __applicationErrorsType?: UpdateItemApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    item: { ...item, id: _id },
  });

  const reqOpts = ambassadorWixPromotePublisherV1Item.updateItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { item: '$[1]' },
        explicitPathsToArguments: { 'item.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'item']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateItem {
  /** Instagram post content. */
  instagramPost?: InstagramPost;
  /** Instagram story content. */
  instagramStory?: InstagramStory;
  /** Facebook post content. */
  facebookPost?: FacebookPost;
  /** Facebook reel content. */
  facebookReel?: FacebookReel;
  /** Facebook story content. */
  facebookStory?: FacebookStory;
  /** YouTube video content. */
  youtubeVideo?: YouTubeVideo;
  /** YouTube short content. */
  youtubeShort?: YouTubeShort;
  /** LinkedIn post content. */
  linkedinPost?: LinkedinPost;
  /**
   * Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @deprecated Deprecated: No longer functional as of July 31, 2026.
   * X (Twitter) post content.
   * @targetRemovalDate 2026-07-31
   */
  twitterPost?: TwitterPost;
  /** Pinterest post content. */
  pinterestPost?: PinterestPost;
  /** Google Business Profile post content. */
  gbpPost?: GbpPost;
  /** TikTok video content. */
  tiktokVideo?: TikTokVideo;
  /** TikTok photo content. */
  tiktokPhoto?: TikTokPhoto;
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Social channel the item is published to, and the account within that channel. */
  channel?: Channel;
  /**
   * Item format. The `type` and the content set in `data` must match a combination supported by the item's `channel.name`:
   *
   * - Instagram: `POST` with `instagramPost`, or `STORY` with `instagramStory`.
   * - Facebook: `POST` with `facebookPost`, `REEL` with `facebookReel`, or `STORY` with `facebookStory`.
   * - YouTube: `VIDEO` with `youtubeVideo`, or `SHORT` with `youtubeShort`.
   * - LinkedIn: `POST` with `linkedinPost`.
   * - X (Twitter): `POST` with `twitterPost`. Deprecated: No longer functional as of July 31, 2026.
   * - Pinterest: `POST` with `pinterestPost`.
   * - Google Business Profile: `POST` with `gbpPost`.
   * - TikTok: `POST` with `tiktokPhoto`, or `VIDEO` with `tiktokVideo`.
   */
  type?: ItemTypeWithLiterals;
  /**
   * Current status of the item in its lifecycle.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Date and time the item was published. Returned only after the item is published.
   * @readonly
   */
  publishedDate?: Date | null;
  /** Scheduling info. Set to schedule the item for a future date instead of publishing it immediately. */
  schedulingInfo?: SchedulingInfo;
  /** Identifies what created the item. */
  consumerInfo?: ConsumerInfo;
  /** Caller-defined reference that uniquely identifies the item. Can be used to prevent duplicate publishing: an attempt to publish a second item with the same `referenceId` fails. Can also be used as a UTM campaign ID. */
  referenceId?: string | null;
  /** Aggregated analytics for the site, attributed to traffic from this post. */
  siteAnalytics?: SiteAnalytics;
  /** Summary of the post's performance on the social channel, such as impressions and interactions. */
  insightsSummary?: InsightsSummary;
  /** Creation and update dates, returned for draft items. */
  draftInfo?: DraftInfo;
  /** Whether the item was generated as an AI suggestion. */
  suggested?: boolean;
  /** Whether the item is a post created natively on the social channel rather than published through Wix. */
  external?: boolean;
  /**
   * Details of why the item failed to publish. Returned only when `status` is `FAILED`.
   * @readonly
   */
  publicationError?: PublicationErrorDetails;
  /**
   * Reference to the site content the post promotes, such as a product, a blog post, or an event.
   * Set per channel.
   */
  siteAssetRef?: AssetReference;
  /**
   * ID of the item this item was derived from, when publishing produced several items
   * from one. Currently set only by `PublishMultiStoryDraft`, which splits a multi-media
   * story draft into one published item per frame: every resulting item carries the
   * originating draft's ID here. Lets a consumer re-correlate the new items with whatever
   * it had recorded against the original draft, which no longer exists after the split.
   * Set by the publisher, never by the caller.
   * @format GUID
   * @readonly
   */
  originItemId?: string | null;
  /**
   * Engagement automations on the published post. Updates replace the whole list. Facebook posts only.
   * @internal
   */
  automationsWrapper?: ItemAutomationsWrapper;
}

/**
 * Changes the scheduled publish date of a scheduled item.
 *
 * Only an item with a `status` of `SCHEDULED` can be rescheduled.
 * @param _id - ID of the scheduled item to reschedule.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId PROMOTE.PUBLISHER_ITEM_UPDATE
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.RescheduleItem
 */
export async function rescheduleItem(
  _id: string,
  options?: RescheduleItemOptions
): Promise<
  NonNullablePaths<
    RescheduleItemResponse,
    | `item.instagramPost.mediaWrapper.media`
    | `item.instagramPost.mediaWrapper.media.${number}.type`
    | `item.instagramPost.insights.likes`
    | `item.instagramPost.insights.engagement`
    | `item.instagramPost.insights.impressions`
    | `item.instagramPost.insights.plays`
    | `item.instagramPost.insights.reach`
    | `item.instagramPost.insights.comments`
    | `item.instagramPost.insights.shares`
    | `item.instagramPost.insights.saved`
    | `item.instagramPost.insights.totalInteractions`
    | `item.instagramStory.insights.impressions`
    | `item.instagramStory.insights.reach`
    | `item.instagramStory.insights.replies`
    | `item.facebookPost.insights.impressions.unique`
    | `item.facebookPost.insights.impressions.total`
    | `item.facebookPost.insights.interactions.unique`
    | `item.facebookPost.insights.interactions.total`
    | `item.facebookPost.insights.reactions.like`
    | `item.facebookPost.insights.reactions.love`
    | `item.facebookPost.insights.reactions.wow`
    | `item.facebookPost.insights.reactions.haha`
    | `item.facebookPost.insights.reactions.sad`
    | `item.facebookPost.insights.reactions.anger`
    | `item.facebookPost.insights.activity.share`
    | `item.facebookPost.insights.activity.like`
    | `item.facebookPost.insights.activity.comment`
    | `item.youtubeVideo.notifySubscribers`
    | `item.youtubeVideo.insights.views`
    | `item.youtubeVideo.insights.comments`
    | `item.youtubeVideo.insights.likes`
    | `item.youtubeVideo.insights.dislikes`
    | `item.youtubeVideo.insights.shares`
    | `item.youtubeShort.notifySubscribers`
    | `item.linkedinPost.insights.uniqueImpressions`
    | `item.linkedinPost.insights.impression`
    | `item.linkedinPost.insights.shares`
    | `item.linkedinPost.insights.engagement`
    | `item.linkedinPost.insights.clicks`
    | `item.linkedinPost.insights.likes`
    | `item.linkedinPost.insights.comments`
    | `item.twitterPost.insights.retweets`
    | `item.twitterPost.insights.replies`
    | `item.twitterPost.insights.likes`
    | `item.twitterPost.insights.quotes`
    | `item.twitterPost.insights.bookmarks`
    | `item.twitterPost.insights.impressions`
    | `item.pinterestPost.insights.impressions`
    | `item.pinterestPost.insights.clicks`
    | `item.pinterestPost.insights.saves`
    | `item.pinterestPost.insights.comments`
    | `item.pinterestPost.insights.reactions`
    | `item.gbpPost.callToAction.actionType`
    | `item.gbpPost.insights.shares`
    | `item.tiktokVideo.privacyLevel`
    | `item.tiktokVideo.disableComment`
    | `item.tiktokVideo.disableDuet`
    | `item.tiktokVideo.disableStitch`
    | `item.tiktokVideo.brandContentToggle`
    | `item.tiktokVideo.brandOrganicToggle`
    | `item.tiktokVideo.insights.views`
    | `item.tiktokVideo.insights.likes`
    | `item.tiktokVideo.insights.comments`
    | `item.tiktokVideo.insights.shares`
    | `item.tiktokPhoto.privacyLevel`
    | `item.tiktokPhoto.disableComment`
    | `item.tiktokPhoto.autoAddMusic`
    | `item.tiktokPhoto.brandContentToggle`
    | `item.tiktokPhoto.brandOrganicToggle`
    | `item.channel.name`
    | `item.type`
    | `item.status`
    | `item.siteAnalytics.traffic`
    | `item.siteAnalytics.pageView`
    | `item.siteAnalytics.siteBounceRate`
    | `item.siteAnalytics.revenue`
    | `item.siteAnalytics.conversionFunnel.cartViewsSessions`
    | `item.siteAnalytics.conversionFunnel.checkOutSessions`
    | `item.siteAnalytics.conversionFunnel.storeOrdersSessions`
    | `item.siteAnalytics.conversionFunnel.bookingsConfirmed`
    | `item.insightsSummary.impressions`
    | `item.insightsSummary.interactions`
    | `item.insightsSummary.impressionsTotal`
    | `item.suggested`
    | `item.external`
    | `item.siteAssetRef.siteAssetRefType`
    | `item.siteAssetRef.name`,
    7
  > & {
    __applicationErrorsType?: RescheduleItemApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    scheduledDate: options?.scheduledDate,
  });

  const reqOpts = ambassadorWixPromotePublisherV1Item.rescheduleItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          scheduledDate: '$[1].scheduledDate',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RescheduleItemOptions {
  /** Date and time to publish the item. If omitted, the item is published immediately. */
  scheduledDate?: Date | null;
}

/**
 * Deletes an item.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId PROMOTE.PUBLISHER_ITEM_DELETE
 * @applicableIdentity APP
 * @returns DeleteItemResponse
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.DeleteItem
 */
export async function deleteItem(_id: string): Promise<
  void & {
    __applicationErrorsType?: DeleteItemApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts = ambassadorWixPromotePublisherV1Item.deleteItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Publishes multiple items in a single call.
 *
 * Each item is published independently. To publish a single item, call Publish Item.
 * @param items - Items to publish.
 * @public
 * @documentationMaturity preview
 * @requiredField items
 * @requiredField items.channel
 * @requiredField items.channel.accountId
 * @permissionId PROMOTE.PUBLISHER_ITEM_CREATE
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.publisher.v1.PublicationService.BulkPublishItems
 */
export async function bulkPublishItems(
  items: NonNullablePaths<Item, `channel` | `channel.accountId`, 3>[],
  options?: BulkPublishItemsOptions
): Promise<
  NonNullablePaths<
    BulkPublishItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.youtubeVideo.notifySubscribers`
    | `results.${number}.item.youtubeShort.notifySubscribers`
    | `results.${number}.item.tiktokVideo.privacyLevel`
    | `results.${number}.item.tiktokVideo.disableComment`
    | `results.${number}.item.tiktokVideo.disableDuet`
    | `results.${number}.item.tiktokVideo.disableStitch`
    | `results.${number}.item.tiktokVideo.brandContentToggle`
    | `results.${number}.item.tiktokVideo.brandOrganicToggle`
    | `results.${number}.item.tiktokPhoto.privacyLevel`
    | `results.${number}.item.tiktokPhoto.disableComment`
    | `results.${number}.item.tiktokPhoto.autoAddMusic`
    | `results.${number}.item.tiktokPhoto.brandContentToggle`
    | `results.${number}.item.tiktokPhoto.brandOrganicToggle`
    | `results.${number}.item.channel.name`
    | `results.${number}.item.type`
    | `results.${number}.item.status`
    | `results.${number}.item.siteAnalytics.traffic`
    | `results.${number}.item.siteAnalytics.pageView`
    | `results.${number}.item.siteAnalytics.siteBounceRate`
    | `results.${number}.item.siteAnalytics.revenue`
    | `results.${number}.item.insightsSummary.impressions`
    | `results.${number}.item.insightsSummary.interactions`
    | `results.${number}.item.insightsSummary.impressionsTotal`
    | `results.${number}.item.suggested`
    | `results.${number}.item.external`
    | `results.${number}.item.siteAssetRef.siteAssetRefType`
    | `results.${number}.item.siteAssetRef.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkPublishItemsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    items: items,
    policy: options?.policy,
    schedulingInfo: options?.schedulingInfo,
    itemExtras: options?.itemExtras,
    returnEntity: options?.returnEntity,
  });

  const reqOpts = ambassadorWixPromotePublisherV1Item.bulkPublishItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          items: '$[0]',
          policy: '$[1].policy',
          schedulingInfo: '$[1].schedulingInfo',
          itemExtras: '$[1].itemExtras',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['items', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkPublishItemsOptions {
  /** Policy that determines how items are published when one of them fails. */
  policy?: BulkPublishItemsPolicyWithLiterals;
  /** Scheduling info applied to all items. If set, the items are scheduled instead of published immediately. */
  schedulingInfo?: SchedulingInfo;
  itemExtras?: ItemExtras;
  /**
   * Whether to return the published items in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}
